import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import classnames from 'classnames'
import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react'

import styles from './stickyWrapper.module.scss'

export interface StickyWrapperProps {
  renderChildren?: (isIntersecting: boolean) => ReactElement
  children?: ReactNode
  top?: string
  stickyWrapperClassName?: string
}

const StickyWrapper = (props: StickyWrapperProps) => {
  const [intersectRef, isIntersecting] = useIntersect(null, '0px', 0, true)
  const [computedTop, setComputedTop] = useState(props.top)
  const isScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)
  const isScrolledDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrolledDown)
  const isSearchMode = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsSearchMode)
  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
  const isMobile = !isDesktop
  useEffect(() => {
    let stickyTop = props.top
    if (!stickyTop) {
      const height = document.querySelector('#site-primary-header')?.getBoundingClientRect().height || 0
      stickyTop = height < 200 && height >= 60 ? `${height}px` : computedTop
    }
    if (stickyWrapperRef.current) {
      const topVal = isScrolledDown && (isScrollingDown ? 0 : computedTop)
      stickyWrapperRef.current.setAttribute('style', `top: ${topVal}; transition: top 0.3s ease-in-out 0s;`)
    }
    setComputedTop(stickyTop)
  }, [props.top, isScrollingDown, isScrolledDown, isSearchMode, computedTop])

  const renderChildren = () => (props.renderChildren ? props.renderChildren(isIntersecting) : props.children || null)
  const stickyWrapperRef = useRef<HTMLDivElement>(null)
  return (
    <>
      <div style={{ position: 'relative' }}>
        <div
          ref={intersectRef}
          style={{
            marginBottom: computedTop,
            width: '100%',
            position: 'absolute',
          }}
        />
      </div>
      <div
        className={getStickyClassname({
          isIntersecting,
          isScrolledDown,
          isScrollingDown,
          isMobile,
          stickyWrapperClassName: props?.stickyWrapperClassName,
        })}
        style={{
          top: getTopVal({ isIntersecting, isScrolledDown, isScrollingDown, computedTop, isMobile }),
          transition: 'top 0.3s ease-in-out 0s',
        }}
      >
        {renderChildren()}
      </div>
      {!isIntersecting && isDesktop && renderChildren()}
    </>
  )
}

const getTopVal = ({
  isIntersecting,
  isScrolledDown,
  isScrollingDown,
  computedTop,
}: {
  isIntersecting: boolean
  isScrolledDown: boolean
  isScrollingDown: boolean
  computedTop?: string
  isMobile: boolean
}) => {
  let topVal = undefined
  if (!isIntersecting) {
    topVal = isScrolledDown && isScrollingDown ? '0px' : computedTop
  }
  return topVal
}

const getStickyClassname = ({
  isIntersecting,
  isScrolledDown,
  isScrollingDown,
  isMobile,
  stickyWrapperClassName,
}: {
  isIntersecting: boolean
  isScrolledDown: boolean
  isScrollingDown: boolean
  computedTop?: string
  isMobile: boolean
  stickyWrapperClassName?: string
}) => {
  // @i18n-ignore
  let stickyClassName = 'undefined'
  if (!isIntersecting && isMobile && !isScrollingDown) {
    stickyClassName = classnames(styles.stickyWrapper, stickyWrapperClassName)
  }
  if (!isIntersecting && !isMobile) {
    stickyClassName = classnames(
      styles.stickyWrapper,
      isScrolledDown && isScrollingDown && styles.stickyWrapper__scrollingDown,
    )
  }
  return stickyClassName
}

export default StickyWrapper
